import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import TitleBar from "../common-components/TitleBar";
import { taxFilingOptions } from "../../utils/constants";
import { useFormik } from "formik";
import { CircularProgress, Grid2 } from "@mui/material";
import CustomButton from "../common-components/CustomButton";
import LLCData from "./LLCData";
import CorpData from "./Corp";
import SelectInputTaxFilling from "./SelectInputTaxFilling";
import { taxFilingSchema } from "../../form-schema";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createTaxFiling, getTaxFilingData } from "../../stores/taxfiling";
import ListLoadingNew from "../common-components/ListLoadingNew";

const TaxFilling = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch();
  const [taxFilingList, setTaxFilingList] = useState<any>(null);
  const [isloading, setIsloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [istaxfree, setIstaxfree] = useState(false);

  const getTaxFilingList = async () => {
    try {
      const payload = { offset: 0, limit: 10 };
      const result = await dispatch<any>(getTaxFilingData(payload));
      setIstaxfree(result?.payload?.is_tax_free);
      setTaxFilingList(result?.payload);

      const subscription = result?.payload?.tax_filing_files_list || [];
      console.log("subscription", subscription);

      const firstSub = subscription[0]; // ✅ take first element safely

      if (subscription?.length > 1) {
        navigate("/tools/tax-filing/list");
        return;
      }

      // Case 1: No subscriptions found
      if (subscription.length === 0 || firstSub?.payment_status === "pending")
        return;

      if (subscription?.length === 1 && !firstSub?.save_as_draft) {
        navigate("/tools/tax-filing/list");
        return;
      }

      // If subscribed and step is 0
      if (firstSub?.is_subscribed && subscription?.length === 1) {
        if (firstSub?.step === 1) {
          if (firstSub?.filling_type === "llc" && firstSub?.have_multimembers) {
            navigate(`/tools/tax-filing/multimember-review/${firstSub?.id}`);
            return;
          } else if (firstSub?.filling_type === "c-corp") {
            navigate(`/tools/tax-filing/review-ccrop/${firstSub?.id}`);
            return;
          } else {
            navigate(`/tools/tax-filing/review/${firstSub?.id}`);
            return;
          }
        }
      }
      if (firstSub?.is_subscribed && subscription?.length === 1) {
        if (firstSub?.step === 1) {
          navigate(`/tools/tax-filing/review/${firstSub?.id}`);
          return;
        }
        if (firstSub?.filling_type === "llc" && firstSub?.have_multimembers) {
          navigate(`/tools/tax-filing/multimember`);
        } else if (firstSub?.filling_type === "c-corp") {
          navigate(`/tools/tax-filing/corp-member`);
        } else {
          navigate(`/tools/tax-filing/single-member`);
        }
      }
    } catch (error) {
      console.error("Error fetching tax filing list:", error);
    } finally {
    }
  };

  const formik = useFormik({
    initialValues: {
      tax_filing: taxFilingOptions[0]?.value || "",
      has_itin: false,
      consent: true,
      multi_members: null,
      total_members: null,
      total_members_itin: null,
      total_amount: 0,
      is_tax_free: taxFilingList?.is_tax_free || false,
    },
    validationSchema: taxFilingSchema,
    onSubmit: async (values) => {
      setIsloading(true);
      const taxFiling = taxFilingOptions.find(
        (option) => option.value === values.tax_filing
      );
      const payload = {
        filling_type:
          taxFiling?.name === "Empty Filing I didnt have any revenue"
            ? "empty_filling"
            : (taxFiling?.name || "").toLowerCase(),
        have_any_itin: values.has_itin,
        amount: values.total_amount,
        have_multimembers: values.multi_members,
        number_of_total_members: values.total_members,
        members_have_itin: values.total_members_itin,
        is_tax_free: istaxfree,
      };
      const result = await dispatch<any>(createTaxFiling(payload));
      if (!istaxfree) {
        if (result?.payload?.status === 1) {
          window.location.href = result?.payload?.data?.url;
        }
      } else {
        if (
          formik?.values?.tax_filing.includes("LLC") &&
          formik?.values?.multi_members
        ) {
          navigate(
            `/tools/tax-filing/multimember/${result?.payload?.data?.id}`
          );
        } else if (formik?.values?.tax_filing.includes("C-Corp")) {
          navigate(
            `/tools/tax-filing/corp-member/${result?.payload?.data?.id}`
          );
        } else {
          navigate(
            `/tools/tax-filing/single-member/${result?.payload?.data?.id}`
          );
        }
      }
      setIsloading(false);
    },
  });

  useEffect(() => {
    if (location.state?.fromButton) {
      navigate(location.pathname, { replace: true, state: {} }); // clear state
      const getList = async () => {
        const payload = { offset: 0, limit: 10 };
        const result = await dispatch<any>(getTaxFilingData(payload));
        setIstaxfree(result?.payload?.is_tax_free);
      };
      getList();
    } else {
      getTaxFilingList();
    }
  }, []);

  useEffect(() => {
    const timeout: any = setTimeout(() => {
      setLoading(false);

      return clearTimeout(timeout);
    }, 4000);
  }, []);

  const taxFiling = taxFilingOptions.find(
    (option) => option.value === formik.values.tax_filing
  );

  // Handle dropdown change and preserve form values
  const handleTaxFilingChange = (value: string) => {
    // Store current form values before changing
    const currentValues = { ...formik.values };

    // Update only the tax_filing field
    formik.setFieldValue("tax_filing", value);

    // Preserve other form values that should persist across tax filing type changes
    setTimeout(() => {
      if (currentValues.has_itin !== undefined) {
        formik.setFieldValue("has_itin", false);
      }
      if (currentValues.consent !== undefined) {
        formik.setFieldValue("consent", true);
      }
      if (currentValues.multi_members !== undefined) {
        formik.setFieldValue("multi_members", null);
      }
      if (currentValues.total_members !== undefined) {
        formik.setFieldValue("total_members", null);
      }
      if (currentValues.total_members_itin !== undefined) {
        formik.setFieldValue("total_members_itin", null);
      }
    }, 0);
  };

  useEffect(() => {
    // Start polling only if payment is pending
    if (
      taxFilingList?.tax_filing_files_list?.[0]?.payment_status === "pending"
    ) {
      const interval = setInterval(() => {
        getTaxFilingList();
      }, 10000);

      // Cleanup: stop polling when component unmounts or status changes
      return () => clearInterval(interval);
    }
  }, [taxFilingList?.tax_filing_files_list?.[0]?.payment_status]);

  if (loading) {
    <ListLoadingNew />;
  }

  return loading ? (
    <ListLoadingNew />
  ) : (
    <form onSubmit={formik.handleSubmit}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <TitleBar title="Tax Filing" />
      </Box>
      <Grid2 container spacing={2}>
        <Grid2
          size={{ xs: 12, md: 7 }}
          sx={{
            marginBottom: "10px",
          }}
        >
          <SelectInputTaxFilling
            formik={formik}
            placeholder="LLC"
            label="LLC or C-Corp *"
            menuData={taxFilingOptions}
            name="tax_filing"
            disabled={false}
            handleCustomData={(e) => handleTaxFilingChange(e.target.value)}
          />
        </Grid2>
      </Grid2>
      <Grid2 container spacing={2}>
        {(taxFiling?.name.includes("LLC") ||
          taxFiling?.name.includes(
            "Empty Filing I didnt have any revenue"
          )) && (
          <LLCData
            formik={formik}
            options={taxFiling}
            is_tax_free={istaxfree}
          />
        )}
        {taxFiling?.name.includes("C-Corp") && (
          <CorpData formik={formik} is_tax_free={istaxfree} showTotal />
        )}
        {/* {taxFiling?.name.includes("Empty Filing") && (
          <EmptyFiling formik={formik} />
        )} */}
      </Grid2>
      <Box
        sx={{
          marginTop: 2,
          display: "flex",
          flexWrap: "wrap",
          //  justifyContent: "flex-start",
          gap: "20px",
          //  flexDirection: {
          //   xs: "column",
          //   sm: "row",
          //  },
        }}
      >
        {taxFilingList?.tax_filing_files_list[0]?.payment_status !==
        "pending" ? (
          <CustomButton
            btnTitle="Buy Now"
            minwidth="191px"
            type="submit"
            loading={isloading}
            handleClick={() => {
              // dispatch<any>(createTaxFiling(formik.values));
            }}
          />
        ) : (
          <div
            style={{
              fontWeight: "500",
              fontSize: "14px",
              color: "#ed0c0cff",
              backgroundColor: "#d7d2d290",
              padding: "10px",
              borderRadius: "7px",
              marginTop: 5,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            Note: Previous payment is under progress. Please allow a few minutes
            for the transaction to complete.
            <CircularProgress size={20} />
          </div>
        )}

        {/* <CustomButton
                  btnTitle="Single form"
                  minwidth="191px"
                  type="submit"
                  loading={false}
                  handleClick={() => {
                    // navigate("/corp-member")
                    // navigate("/multimember")
                    navigate("/tools/tax-filing/single-member");
                  }}
                 />
                 <CustomButton
                  btnTitle="C-Corp form"
                  minwidth="191px"
                  type="submit"
                  loading={false}
                  handleClick={() => {
                    navigate("/tools/tax-filing/corp-member")
                    // navigate("/multimember")
                    // navigate("/single-member");
                  }}
                 />
                 <CustomButton
                  btnTitle="Multi-member form"
                  minwidth="191px"
                  type="submit"
                  loading={false}
                  handleClick={() => {
                    // navigate("/corp-member")
                    navigate("/tools/tax-filing/multimember")
                    // navigate("/single-member");
                  }}
                 />*/}
      </Box>
    </form>
  );
};

export default TaxFilling;
